import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { isSameDay, format } from 'date-fns';
import { type DateRange } from 'react-day-picker';

interface CalendarViewProps {
  dateRange: DateRange | undefined;
  selectedDate: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
  disabledDates?: Date[];
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  dateRange,
  selectedDate,
  onSelectDate,
  disabledDates = []
}) => {
  // Create a function to disable dates outside the range
  const isDateDisabled = (date: Date) => {
    // Check if date is outside the range
    if (dateRange?.from && date < dateRange.from) return true;
    if (dateRange?.to && date > dateRange.to) return true;
    
    // Disable specific dates
    if (disabledDates.some(disabledDate => isSameDay(date, disabledDate))) {
      return true;
    }
    
    return false;
  };

  // Create a handler that manages type conversion for the calendar
  const handleSelectDate = (value: DateRange | Date | Date[] | undefined) => {
    // If it's a single Date, pass it to the callback
    if (value instanceof Date) {
      onSelectDate(value);
    } 
    // If it's undefined, pass undefined
    else if (value === undefined) {
      onSelectDate(undefined);
    }
    // Ignore other types (DateRange or Date[])
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-lg font-semibold mb-4">Select Date</h2>
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={handleSelectDate}
        disabled={isDateDisabled}
        className="rounded-md"
      />
      <div className="mt-2 text-center text-sm text-muted-foreground">
        Selected: <span className="font-medium">{selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'None'}</span>
      </div>
    </div>
  );
};

// Export both named and default for flexibility
export default CalendarView; 